"use client"

import React from "react"
import AppIcon from "@/app/components/atomic/AppIcon"
import ChoiceChips from "@/app/components/atomic/molecules/ChoiceChips"
import { TravelMotif } from "@/app/stores/travel/travel-comparator.store"
import { rovingRadioGroup } from "@/app/utils/roving-radio-group"
import { TRAVEL_MOTIF_OPTIONS } from "../travelFormConfig"

const MOTIF_VALUES = TRAVEL_MOTIF_OPTIONS.map((option) => option.value)

interface Props {
    value: TravelMotif
    onChange: (motif: TravelMotif) => void
}

const MotifIcon = ({ icon, selected }: { icon: string; selected: boolean }) => (
    <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-[10px] ${selected ? "bg-white" : "bg-neutral"}`}
        aria-hidden="true"
    >
        <AppIcon iconClassName={icon} size="md" className="text-deep-green" />
    </span>
)

// Tiles on desktop, a horizontally scrolling chip row on mobile to save height
export default function TravelMotifField({ value, onChange }: Props) {
    const radioGroup = rovingRadioGroup(MOTIF_VALUES, value, onChange)

    return (
        <div className="flex flex-col gap-2.5">
            <div
                role="radiogroup"
                aria-label="Type de séjour"
                onKeyDown={radioGroup.onKeyDown}
                className="hidden gap-2.5 sm:grid sm:grid-cols-3"
            >
                {TRAVEL_MOTIF_OPTIONS.map((option) => {
                    const isSelected = option.value === value
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={radioGroup.tabIndexFor(option.value)}
                            data-test={`travel-motif-${option.value}`}
                            onClick={() => onChange(option.value)}
                            className={`flex min-h-[52px] cursor-pointer items-center gap-2.5 rounded-[14px] border-2 px-3 py-2 text-left text-sm text-pale transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${
                                isSelected ? "border-rm-blue bg-rm-blue/10 font-bold" : "border-[#EFE6E2] bg-white hover:border-rm-gray"
                            }`}
                        >
                            <MotifIcon icon={option.icon} selected={isSelected} />
                            <span className="leading-tight">{option.label}</span>
                        </button>
                    )
                })}
            </div>

            <div className="flex flex-col gap-2 sm:hidden">
                <span id="travel-motif-chips-label" className="text-[13px] font-semibold text-pale">
                    Type de séjour
                </span>
                <ChoiceChips
                    options={TRAVEL_MOTIF_OPTIONS}
                    value={value}
                    onChange={onChange}
                    ariaLabelledBy="travel-motif-chips-label"
                    testIdPrefix="travel-motif"
                    className="-mx-[18px] scroll-px-[18px] px-[18px] pb-0.5"
                />
            </div>
        </div>
    )
}
